import { useState, useRef } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { userService } from '../api/users';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import { Camera, User, Lock, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function Profile() {
    const { user, setUser } = useAuth(); // Assuming setUser updates context state
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Profile State
    const [profileData, setProfileData] = useState({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        username: user?.username || '',
    });

    // Password State
    const [passwords, setPasswords] = useState({
        old_password: '',
        new_password: '',
        confirm_password: ''
    });

    const handleProfileChange = (e) => {
        setProfileData({ ...profileData, [e.target.name]: e.target.value });
    };

    const handlePasswordChange = (e) => {
        setPasswords({ ...passwords, [e.target.name]: e.target.value });
    };

    const onAvatarClick = () => {
        fileInputRef.current.click();
    };

    const handleAvatarUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('avatar', file);

        try {
            setIsLoading(true);
            const updatedUser = await userService.updateProfile(formData);
            setUser(updatedUser); // Update context
            showMessage('success', 'Фото профиля обновлено');
        } catch (error) {
            console.error(error);
            showMessage('error', 'Ошибка загрузки фото');
        } finally {
            setIsLoading(false);
        }
    };

    const saveProfile = async (e) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            const updatedUser = await userService.updateProfile(profileData);
            setUser(updatedUser);
            showMessage('success', 'Профиль обновлен');
        } catch (error) {
            console.error(error);
            showMessage('error', 'Ошибка сохранения');
        } finally {
            setIsLoading(false);
        }
    };

    const updatePassword = async (e) => {
        e.preventDefault();
        if (passwords.new_password !== passwords.confirm_password) {
            showMessage('error', 'Новые пароли не совпадают');
            return;
        }

        try {
            setIsLoading(true);
            await userService.changePassword(passwords);
            setPasswords({ old_password: '', new_password: '', confirm_password: '' });
            showMessage('success', 'Пароль успешно изменен');
        } catch (error) {
            console.error(error);
            const errorMsg = error.response?.data?.old_password?.[0] || 'Ошибка смены пароля';
            showMessage('error', errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8 pb-12">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('menu_profile')}</h1>
                <p className="text-sm text-gray-500 mt-1">Управление личными данными</p>
            </div>

            {/* Message Alert */}
            {message.text && (
                <div className={`p-4 rounded-xl flex items-center ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                    {message.type === 'success' ? <CheckCircle className="w-5 h-5 mr-2" /> : <AlertCircle className="w-5 h-5 mr-2" />}
                    {message.text}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Avatar & Basic Info */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 text-center">
                        <div className="relative inline-block mb-4 group cursor-pointer" onClick={onAvatarClick}>
                            <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-white shadow-md mx-auto">
                                {user?.avatar ? (
                                    <img src={user.avatar} alt="Avatar" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                                        <User className="w-16 h-16" />
                                    </div>
                                )}
                            </div>
                            <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <Camera className="w-8 h-8 text-white" />
                            </div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/*"
                                onChange={handleAvatarUpload}
                            />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">
                            {user?.first_name || user?.username} {user?.last_name}
                        </h2>
                        <p className="text-sm text-gray-500 capitalize">{t(`role_${user?.role}`)}</p>
                    </div>
                </div>

                {/* Right Column: Forms */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Personal Info Form */}
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-8 h-8 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center mr-3">
                                <User className="w-5 h-5" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">Личные данные</h3>
                        </div>
                        <form onSubmit={saveProfile} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input
                                    label="Имя"
                                    name="first_name"
                                    value={profileData.first_name}
                                    onChange={handleProfileChange}
                                    placeholder="Ваше имя"
                                />
                                <Input
                                    label="Фамилия"
                                    name="last_name"
                                    value={profileData.last_name}
                                    onChange={handleProfileChange}
                                    placeholder="Ваша фамилия"
                                />
                            </div>
                            <Input
                                label="Username"
                                name="username"
                                value={profileData.username}
                                disabled
                                className="bg-gray-50 cursor-not-allowed"
                            />
                            <div className="pt-2 flex justify-end">
                                <Button type="submit" disabled={isLoading} variant="primary">
                                    <Save className="w-4 h-4 mr-2" /> Sohranish
                                </Button>
                            </div>
                        </form>
                    </div>

                    {/* Password Change Form */}
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-8 h-8 bg-orange-50 text-orange-600 rounded-lg flex items-center justify-center mr-3">
                                <Lock className="w-5 h-5" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">Безопасность</h3>
                        </div>
                        <form onSubmit={updatePassword} className="space-y-4">
                            <Input
                                label="Текущий пароль"
                                type="password"
                                name="old_password"
                                value={passwords.old_password}
                                onChange={handlePasswordChange}
                                required
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input
                                    label="Новый пароль"
                                    type="password"
                                    name="new_password"
                                    value={passwords.new_password}
                                    onChange={handlePasswordChange}
                                    required
                                />
                                <Input
                                    label="Подтверждение"
                                    type="password"
                                    name="confirm_password"
                                    value={passwords.confirm_password}
                                    onChange={handlePasswordChange}
                                    required
                                />
                            </div>
                            <div className="pt-2 flex justify-end">
                                <Button type="submit" disabled={isLoading} variant="secondary">
                                    <Save className="w-4 h-4 mr-2" /> Обновить пароль
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
